import XRegExp from "xregexp";
import { DateTime, Settings } from "luxon";
import { isEmpty, some, memoize, toUpper, uniq, map } from "../utils/lodash";
import { AnswerRecalledValue } from "./AnswerRecalledValue";
import { RecallType } from "./RecallType";
import { RecalledValue } from "./RecalledValue";
import { SystemRecalledValue } from "./SystemRecalledValue";
import { DateRecalledValue } from "./DateRecalledValue";
import { TimeRecalledValue } from "./TimeRecalledValue";
import { TimeSpanRecalledValue } from "./TimeSpanRecalledValue";
import { VariablesState } from "../models/states/VariablesState";
import { InterviewState } from "../models/states/InterviewState";
import { Log } from "../utils/logging";
import { QuestionType } from "../models/enums/QuestionType";
import { getSettingBoolean, getSettingString } from "../models/utilities/settingUtilities";
import { DateTimeRecalledValue } from "./DateTimeRecalledValue";
import { RespondentModel } from "../models/RespondentModel";
import { isNullOrEmpty } from "../utils";
import { getVariableRow } from "../redux/reducers/interview/navigationUtilities";
import { NavigationState } from "../models/states/NavigationState";
import {
    getQuestionWithVariable,
    getVariableWithName,
} from "../models/utilities/questionnaireUtilities";
import { QuestionnaireModel } from "../models/QuestionnaireModel";
import { dateFormat } from "../models/utilities/questionUtilities";
import { cDateFormatToLuxon } from "../utils/time";
import { getMentions } from "../redux/reducers/interview/variablesUtilities";
import { DayRecalledValue } from "./DayRecalledValue";
import { getCoordinates } from "../models/utilities/respondentUtilities";

const TAG = "piping";

const QUESTION_VARIABLE_NAME_REGEX_VALUE = "(?<name>[a-z0-9_]+)";
const SYSTEM_VARIABLE_NAME_REGEX_VALUE = "(?<name>\\$[a-z0-9]+)";

const MENTION_REGEX_VALUE = "(\\(|%28)(?<mention>[a-z0-9-]+)(\\)|%29)";
const MATRIX_REGEX_VALUE = "(\\.Row)(\\(|%28)(?<matrix>[a-z0-9\\-]+)(\\)|%29)";
const FORMAT_REGEX_VALUE = "(\\.Format)(\\(|%28)(?<format>[a-zA-Z \\,\\:\\-\\/]+)(\\)|%29)";

const VARIABLE_PROPERTY = "\\.(?<property>[a-z]+)((\\(|%28)(?<param>[a-z0-9/:\\-]+)(\\)|%29))?";

const QUESTION_VARIABLE = XRegExp(
    `\\[${QUESTION_VARIABLE_NAME_REGEX_VALUE}(${MATRIX_REGEX_VALUE})?(${FORMAT_REGEX_VALUE})?(${MENTION_REGEX_VALUE})?(?<properties>(${VARIABLE_PROPERTY})*)\\]`,
    "in",
);
const SYSTEM_VARIABLE = XRegExp(
    `\\[${SYSTEM_VARIABLE_NAME_REGEX_VALUE}(${FORMAT_REGEX_VALUE})?(?<properties>(${VARIABLE_PROPERTY})*)\\]`,
    "in",
);

export class Piping {
    private static getProperties(match: any) {
        const matches: any[] = [];
        XRegExp.forEach(match.properties, XRegExp(VARIABLE_PROPERTY, "gi"), (match) =>
            matches.push(match),
        );
        if (match.mention != null) matches.unshift({ property: "answer", param: [match.mention] });
        if (match.format != null) matches.unshift({ property: "format", param: [match.format] });
        if (matches.length === 0) matches.unshift({ property: "answer" });
        return matches;
    }

    private static getDefaultValue(match: any, mode: RecallType) {
        if (!isNullOrEmpty(match.property))
            return match.property.toLowerCase() === "code" ? "0" : "";
        return mode === RecallType.Code ? "0" : "";
    }

    private static recallSystemMatch(
        match: any,
        variables: VariablesState,
        respondent?: RespondentModel,
    ): string | RecalledValue {
        let variableName = match.name.toUpperCase();
        if (!variableName.startsWith("$")) variableName = `$${variableName}`;
        Settings.defaultLocale = respondent.language;
        const t = DateTime.utc();
        const tz = DateTime.local();
        switch (variableName) {
            case "$D":
                return new DateRecalledValue(tz);
            case "$H":
                return new TimeRecalledValue(tz);
            case "$UTCDATE":
                return new DateRecalledValue(t);
            case "$UTCTIME":
                return new TimeRecalledValue(t);
            case "$T":
                return new TimeSpanRecalledValue(respondent?.sessionStartTime);
            case "$J":
                return new DayRecalledValue(tz);
            case "$R":
                return respondent && respondent.dispositionCode
                    ? String(respondent.dispositionCode)
                    : "";
            case "$PHONE":
                return respondent ? respondent.phone || "" : "";
            case "$EMAIL":
                return respondent ? respondent.email || "" : "";
            case "$GEOLOCATION":
                return getCoordinates(respondent);
            default:
                return new SystemRecalledValue(variableName, variables);
        }
    }

    private static recallQuestionMatch(
        match: any,
        variables: VariablesState,
        navigation: NavigationState,
        questionnaire: QuestionnaireModel,
        respondent?: RespondentModel,
        language?: string,
        mode?: RecallType,
    ): string | RecalledValue {
        const variableName = match.name.toUpperCase();
        if (mode === RecallType.Text && isEmpty(getVariableWithName(questionnaire, variableName)))
            return match.toString();
        const matrixIndex =
            match.matrix === undefined
                ? getVariableRow(navigation, questionnaire, variableName)
                : Number(match.matrix);
        const mentionIndex = !match.mention ? 0 : Number(match.mention) - 1;
        // Get mentions from state.
        const mentions = getMentions(variables, variableName, matrixIndex);
        if (mentions === undefined || mentionIndex >= mentions.length || mentionIndex < 0)
            return null;
        // Check if the question is a date.
        const question = getQuestionWithVariable(questionnaire, variableName);

        if (question != null && mentions.length > 0) {
            switch (question.type) {
                case QuestionType.DateTimeAnswer: {
                    Settings.defaultLocale = respondent.language;
                    // If value is an integer, then it has already been evaluated.
                    if (/^-?\d+$/.test(mentions[0].value)) {
                        return mentions[0].value;
                    }
                    if (getSettingBoolean(question, "Type", "DateTime"))
                        return new DateTimeRecalledValue(
                            DateTime.fromFormat(mentions[0].value, dateFormat(question)),
                        );
                    if (getSettingBoolean(question, "Type", "Time")) {
                        const timeFormat = getSettingString(question, "TimeFormat", "HH:mm");
                        return new TimeRecalledValue(
                            DateTime.fromFormat(mentions[0].value, cDateFormatToLuxon(timeFormat)),
                        );
                    }
                    return new DateRecalledValue(
                        DateTime.fromFormat(mentions[0].value, dateFormat(question)),
                    );
                }
                case QuestionType.FileUpload: {
                    const file = JSON.parse(mentions[0].value);
                    return file ? file.fileName : "";
                }
                default:
                    break;
            }
        }

        return new AnswerRecalledValue(
            mentions,
            respondent.language || language,
            questionnaire,
            variableName,
        );
    }

    private static evaluateRecalledValue(
        match: any,
        recall: () => string | RecalledValue,
        mode: RecallType = RecallType.Code,
    ): string {
        try {
            let value: string | RecalledValue = recall();
            // If mention does not exist, an empty string is returned in text mode (0 in code mode)
            if (!value) return this.getDefaultValue(match, mode);
            const properties = this.getProperties(match);
            if (some(properties, (p) => p.property.toLowerCase() === "text"))
                mode = RecallType.Text;
            while (value instanceof RecalledValue) {
                // Evaluate all properties until a string is returned.
                if (properties.length > 0) {
                    const property = properties.shift();
                    const params = property.param !== undefined ? [property.param] : undefined;
                    value = value.evaluateProperty(mode, property.property.toLowerCase(), params);
                    continue;
                }
                // If the value is still recalled, evaluate it to a string.
                value = value.toString(mode);
            }
            return value === undefined ? "" : value;
        } catch (ex) {
            Log.e(TAG, "RECALL_ERROR", `An error occurred while evaluating "${match}"`, ex);
            return "";
        }
    }

    static identifyVariables = memoize((value: string) => {
        const variables: string[] = [];
        value = XRegExp.replace(
            value,
            SYSTEM_VARIABLE,
            (match: any) => {
                variables.push(match.name);
                return match.name;
            },
            "all",
        );
        value = XRegExp.replace(
            value,
            QUESTION_VARIABLE,
            (match: any) => {
                variables.push(match.name);
                return match.name;
            },
            "all",
        );
        const uniqueVariables = uniq(variables);
        return map(uniqueVariables, toUpper);
    });

    static evaluateVariable(
        interview: InterviewState,
        name: string,
        mode: RecallType = RecallType.Code,
    ): string {
        const match = { name };
        return this.evaluateRecalledValue(
            match,
            () =>
                this.recallQuestionMatch(
                    match,
                    interview.variables,
                    interview.navigation,
                    interview.questionnaire,
                    interview.respondent,
                    interview.language,
                    mode,
                ),
            mode,
        );
    }

    static evaluateRecalls(
        interview: InterviewState,
        value: string,
        mode: RecallType = RecallType.Code,
    ): string {
        if (value === null || value === undefined) return value;
        value = XRegExp.replace(
            value,
            SYSTEM_VARIABLE,
            (match: any) =>
                this.evaluateRecalledValue(
                    match,
                    () => this.recallSystemMatch(match, interview.variables, interview.respondent),
                    mode,
                ),
            "all",
        );
        value = XRegExp.replace(
            value,
            QUESTION_VARIABLE,
            (match: any) =>
                this.evaluateRecalledValue(
                    match,
                    () =>
                        this.recallQuestionMatch(
                            match,
                            interview.variables,
                            interview.navigation,
                            interview.questionnaire,
                            interview.respondent,
                            interview.language,
                            mode,
                        ),
                    mode,
                ),
            "all",
        );
        return value;
    }
}
